import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Info } from "lucide-react";
import { PlayoutSettings } from "./PlayoutSettings";
import { PlayoutAssets } from "./PlayoutAssets";
import { PlayoutList } from "./PlayoutList";
import { PlaylistPanel } from "./PlaylistPanel";
import { PlayoutRunPanel } from "./PlayoutRunPanel";
import { PlayoutLog } from "./PlayoutLog";

interface PlayoutTabProps {
  bridgeUrl: string | undefined | null;
  atemConnections: Array<{ id: string; name: string }>;
  vmixConnections: Array<{ id: string; name: string }>;
}

/**
 * Playout: scheduled, logged interruptions of the program feed. Two types, both
 * driven by the same squeeze: an L-shape still from the ATEM media pool, and a
 * video cut-in played out by vMix.
 */
export function PlayoutTab({
  bridgeUrl,
  atemConnections,
  vmixConnections,
}: PlayoutTabProps) {
  if (!bridgeUrl) {
    return (
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          This studio has no bridge URL configured, so playout is unavailable.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <PlayoutRunPanel bridgeUrl={bridgeUrl} />

      <Tabs defaultValue="playlists">
        <TabsList>
          <TabsTrigger value="playlists" data-testid="playout-tab-playlists">
            Playlists
          </TabsTrigger>
          <TabsTrigger value="playouts" data-testid="playout-tab-playouts">
            Playouts
          </TabsTrigger>
          <TabsTrigger value="artwork" data-testid="playout-tab-artwork">
            Artwork
          </TabsTrigger>
          <TabsTrigger value="log" data-testid="playout-tab-log">
            Log
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="playout-tab-settings">
            Settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="playlists" className="mt-4">
          <PlaylistPanel bridgeUrl={bridgeUrl} />
        </TabsContent>
        <TabsContent value="playouts" className="mt-4">
          <PlayoutList bridgeUrl={bridgeUrl} />
        </TabsContent>
        <TabsContent value="artwork" className="mt-4">
          <PlayoutAssets bridgeUrl={bridgeUrl} />
        </TabsContent>
        <TabsContent value="log" className="mt-4">
          <PlayoutLog bridgeUrl={bridgeUrl} />
        </TabsContent>
        <TabsContent value="settings" className="mt-4">
          <PlayoutSettings
            bridgeUrl={bridgeUrl}
            atemConnections={atemConnections}
            vmixConnections={vmixConnections}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
